import { ItemIDs } from '../../../../Config/Items';
import { MISSION_REFRESH_TIME } from '../../../../Config/Missions';
import Core from '../../../../Core/Core';
import { HudActions, HudEmitter } from '../../../../Managers/Hud/HudEmitter';
import { IDailyMission, MissionState, MissionTypes } from '../../../../State/reducers/missions';
import { waitForMs } from '../../../../Utils/AsyncUtils';
import DateUtils from '../../../../Utils/DateUtils';
import { createPixiText } from '../../../../Utils/Utils';
import { AdButton } from '../../../Shared/Buttons/AdButton';
import { MissionProgress } from './MissionProgress';
import { MissionsCoinsCollect } from './MissionsCoinsCollect';

export class MissionTab extends PIXI.Container {
    private mission: IDailyMission;
    private refreshButton: AdButton;
    private summaryText: PIXI.Text;
    private collectText: PIXI.Text;
    private missionIcon: PIXI.Sprite;
    private missionCheck: PIXI.Sprite;
    private missionProgress: MissionProgress;
    private coinsCollectBtn: MissionsCoinsCollect;

    constructor(mission: IDailyMission, width: number, height: number) {
        super();

        this.mission = mission;

        this.x = -width / 2;

        // nineslice bg
        const texture = PIXI.utils.TextureCache['mapui/nineslice-tab-bg'];
        const bg = new PIXI.mesh.NineSlicePlane(texture, 10, 10, 10, 10);

        bg.width = width;
        bg.height = height;

        // icon
        this.missionIcon = new PIXI.Sprite(PIXI.utils.TextureCache[this.getMissionIconId(mission)]);
        this.missionIcon.anchor.set(0.5, 0.5);
        this.missionIcon.x = this.missionIcon.width * 0.35 + bg.width * 0.05;
        this.missionIcon.y = bg.height * 0.6;
        this.missionIcon.scale.set(0.95);

        // summary text
        this.summaryText = createPixiText(
            this.getMissionSummary(mission),
            new PIXI.Rectangle(bg.width * 0.05, bg.height * 0.2, bg.width * 0.7, bg.height * 0.4),
            'Baloo',
            23
        );
        this.summaryText.anchor.set(0, 0.5);
        this.summaryText.style.leading = -10;
        this.summaryText.style.align = 'left';

        // progress bar
        this.missionProgress = new MissionProgress(mission.targetNum, this.getShowCurrent(mission));
        this.missionProgress.x = bg.width * 0.5;
        this.missionProgress.y = bg.height * 0.45;
        this.missionProgress.updateProgress(mission.total, mission.targetNum);

        // check mark
        this.missionCheck = new PIXI.Sprite(PIXI.utils.TextureCache['mapui/checkmark']);
        this.missionCheck.anchor.set(0.5);

        this.missionCheck.x =
            this.summaryText.x + this.summaryText.width + this.missionCheck.width * 0.5;

        this.missionCheck.y = this.summaryText.y;
        this.missionCheck.scale.set(0.85);
        this.missionCheck.alpha = 0;

        this.coinsCollectBtn = new MissionsCoinsCollect(mission.tier, mission.uid);
        this.coinsCollectBtn.x = bg.width - this.coinsCollectBtn.width * 0.45 - bg.width * 0.01;
        this.coinsCollectBtn.y = bg.height * 0.55;
        this.coinsCollectBtn.disable();

        this.collectText = createPixiText(
            Core.local.getText('TapToCollect'),
            new PIXI.Rectangle(bg.width * 0.5, bg.height * 0.7, bg.width * 0.7, bg.height * 0.4),
            'Baloo',
            23,
            23
        );
        this.collectText.alpha = 0;

        this.addChild(
            bg,
            //@ts-ignore
            this.missionIcon,
            this.missionProgress,
            this.summaryText,
            this.missionCheck,
            this.coinsCollectBtn,
            this.collectText
        );

        if (Core.sdk.ads.getRewardedEnabled()) {
            this.refreshButton = new AdButton(
                width * 0.6,
                height * 0.35,
                Core.local.getText('SkipMission')
            );
            this.refreshButton.x = bg.width * 0.5;
            this.refreshButton.y = bg.height - this.refreshButton.height * 0.75;
            this.refreshButton.alpha = 1; // set active
            this.refreshButton.interactive = true;
            this.refreshButton.buttonMode = true;
            this.refreshButton.on('trigger', () => {
                HudEmitter.instance.emit(HudActions.MISSION_SELECT_REFRESH, this.mission.uid);
            });
            this.addChild(this.refreshButton);
        }

        if (mission.missionState === MissionState.REFRESHING) {
            this.showMissionRefresh();
        }

        if (mission.missionState === MissionState.COMPLETE) {
            this.showMissionComplete(mission);
        }

        // this.interactive = false;
        // this.buttonMode = false;
    }

    public updateMissionInfo(mission: IDailyMission, refreshTime: number) {
        if (mission.uid !== this.mission.uid) {
            this.initMissionDisplay(mission);
        } else {
            if (mission.missionState !== this.mission.missionState) {
                if (mission.missionState === MissionState.COMPLETE) {
                    this.showMissionComplete(mission);
                }

                if (mission.missionState === MissionState.REFRESHING) {
                    this.showMissionRefresh();
                }
            }

            if (mission.missionState === MissionState.REFRESHING) {
                const timeRemaining = DateUtils.timeRemainingTo(MISSION_REFRESH_TIME + refreshTime);
                const countdownString = DateUtils.format(timeRemaining, 'hh:mm:ss');
                this.missionProgress.showTimeCount(countdownString);
            }

            if (mission.missionState === MissionState.ACTIVE) {
                this.missionProgress.updateProgress(
                    mission.total,
                    mission.targetNum,
                    this.getShowCurrent(mission)
                );
            }
        }

        this.mission = mission;
    }

    private initMissionDisplay(mission: IDailyMission) {
        this.missionIcon.texture = PIXI.utils.TextureCache[this.getMissionIconId(mission)];
        this.summaryText.text = this.getMissionSummary(mission);
        this.missionProgress.updateProgress(0, mission.targetNum, this.getShowCurrent(mission));

        this.missionCheck.alpha = 0;
        this.collectText.alpha = 0;
        this.missionIcon.alpha = 1;
        this.coinsCollectBtn.alpha = 1;
        this.coinsCollectBtn.disable();
        this.coinsCollectBtn.changeId(mission.uid);

        if (this.refreshButton) {
            this.refreshButton.alpha = 1;
            this.refreshButton.interactive = true;
            this.refreshButton.buttonMode = true;
        }
    }

    private showMissionComplete(mission: IDailyMission) {
        this.coinsCollectBtn.enable();
        this.missionCheck.alpha = 1;
        if (this.refreshButton) {
            this.refreshButton.interactive = false;
            this.refreshButton.buttonMode = false;
            this.refreshButton.alpha = 0;
        }

        this.collectText.alpha = 1;
        this.missionProgress.updateProgress(mission.targetNum, mission.targetNum);

        this.interactive = true;

        this.buttonMode = true;
        this.on('pointerdown', this.collectCoins.bind(this));
    }

    private showMissionRefresh() {
        this.missionIcon.alpha = 0;
        this.missionCheck.alpha = 0;
        this.collectText.alpha = 0;
        this.coinsCollectBtn.alpha = 0;
        this.coinsCollectBtn.disable();
        if (this.refreshButton) {
            this.refreshButton.interactive = false;
            this.refreshButton.buttonMode = false;
            this.refreshButton.alpha = 0;
        }

        this.summaryText.text = Core.local.getText('NewMissionIn');
        // this.interactive = false;
        // this.buttonMode = false;
    }

    private getMissionIconId(mission: IDailyMission): string {
        let iconId = 'mapui/goal-levels-complete'; // works for beat or complete levels
        const a = mission.action.toLowerCase();

        if (a === MissionTypes.BEAT) {
            iconId = 'mapui/goal-levels';
        }
        if (a === MissionTypes.COLLECT) {
            if (mission.targetId === 'stars') {
                iconId = 'mapui/goal-stars';
            }

            if (mission.targetId === 'toys') {
                iconId = 'mapui/goal-toys';
            }
        }

        if (a === MissionTypes.USE) {
            iconId = 'mapui/booster-' + mission.targetId.toLowerCase();
        }

        if (a === MissionTypes.MATCH) {
            iconId = 'mapui/goal-tiles';
        }

        if (a === MissionTypes.MATCHGOLD) {
            iconId = 'mapui/goal-gold';
        }

        if (a === MissionTypes.REACH) {
            iconId = 'mapui/goal-combo';
        }

        return iconId;
    }

    private getMissionSummary(mission: IDailyMission): string {
        let summary = ''; // works for beat or complete levels
        const a = mission.action.toLowerCase();
        if (a === MissionTypes.COLLECT) {
            if (mission.targetId === 'stars') {
                summary = Core.local.getText('MissionCollectStars');
            } else {
                summary = Core.local.getText('MissionCollectToys');
            }
        }

        if (a === MissionTypes.USE) {
            if (mission.targetId === ItemIDs.HINT) {
                summary = Core.local.getText('MissionUseHint');
            } else if (mission.targetId === ItemIDs.FIRECRACKER) {
                summary = Core.local.getText('MissionUseFirecracker');
            } else if (mission.targetId === ItemIDs.MAGNET) {
                summary = Core.local.getText('MissionUseMagnet');
            }
        }

        if (a === MissionTypes.MATCH) {
            summary = Core.local.getText('MissionMatchTiles');
        }

        if (a === MissionTypes.MATCHGOLD) {
            summary = Core.local.getText('MissionMatchGold');
        }

        if (a === MissionTypes.BEAT) {
            summary = Core.local.getText('MissionBeatLevel');
        }

        if (a === MissionTypes.COMPLETE) {
            summary = Core.local.getText('MissionCompleteLevels');
        }

        if (a === MissionTypes.REACH) {
            summary = Core.local.getText('MissionReachCombo');
        }

        if (summary.includes('[X]')) {
            summary = summary.replace('[X]', mission.targetNum.toString(10));
        }

        if (summary.includes('[x]')) {
            summary = summary.replace('[x]', mission.targetNum.toString(10));
        }

        return summary;
    }

    private getShowCurrent(mission: IDailyMission) {
        return (
            mission.action.toLowerCase() !== MissionTypes.REACH &&
            mission.action.toLowerCase() !== MissionTypes.BEAT
        );
    }

    private async collectCoins() {
        this.removeAllListeners();
        this.interactive = false;
        this.buttonMode = false;

        const coins = this.mission.tier === 1 ? 30 : this.mission.tier === 2 ? 60 : 100;
        this.coinsCollectBtn.showClick();
        await waitForMs(500);

        HudEmitter.instance.emit(HudActions.MISSION_COLLECT_COINS, [coins, this.mission.uid]);
    }
}
